import { useLayoutEffect, type RefObject } from 'react';

const REVEAL_SELECTOR = '[data-reveal]';

export function useScrollReveal(rootRef: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const elements = Array.from(root.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.setAttribute('data-revealed', ''));
      return;
    }

    const visibleBoundary = window.innerHeight * 0.7;
    elements.forEach((element) => {
      const bounds = element.getBoundingClientRect();
      if (bounds.top < visibleBoundary && bounds.bottom > 0)
        element.setAttribute('data-revealed', '');
    });

    root.setAttribute('data-scroll-reveal-ready', '');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.setAttribute('data-revealed', '');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -28% 0px',
        threshold: 0.01,
      },
    );

    elements.forEach((element) => {
      if (!element.hasAttribute('data-revealed')) observer.observe(element);
    });

    return () => {
      observer.disconnect();
      root.removeAttribute('data-scroll-reveal-ready');
    };
  }, [rootRef]);
}
